import * as THREE from 'three'
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js'
import { getToonRamp } from './toonRamp'
import { chooseTextureSource, shouldSwapTexture } from './textureVariant.js'
import { getTextureOverride, beginUpgradeProgress, tickUpgradeProgress, endUpgradeProgress } from './textureQuality.js'
import { applyTextureAdjust, setTextureAdjustUniform } from './textureAdjustChunk'
import { compileMaterialGraph, assembleGraphShader, assembleGraphVertex, assembleToonShader } from './materialGraphCompiler.js'
import { applyDefaultTextureWrap } from './textureWrap.js'
import { graphBlendMode } from './graphBlendMode.js'
import {
  classifyMeshBinding, isUnbound, emptyBindingReport, recordBinding,
  bindingSignature, bindingMessages, HIDDEN,
} from './materialBinding.js'
import { unboundMaterial } from './unboundMaterial.js'

// material_assets-driven material builder. Replaces the legacy role-based
// factories that lived here pre-Phase-7. Every character now renders
// through buildMaterialsFromAssets({ materials, textures, mapping }):
//
//   materials   — material_assets rows from the server
//   textures    — texture_assets rows (UUID → file_url lookup)
//   mapping     — [{ meshIndex, meshName, materialId?, hidden? }, …]
//
// AvatarModel calls applyMaterialMapping(rigScene, built) which walks
// every mesh in the rig and assigns the right Three material by node
// name. EnvProbe reads the flat material list from getAvatarMaterials()._all
// to apply envMap to all of them in one pass.

// ─── Texture cache ─────────────────────────────────────────────────────────

const _texLoader = new THREE.TextureLoader()
const _textureCache = new Map() // (url + sRGB|Linear) → THREE.Texture

// Per-texture provenance for the quality cascade (ENG-757). A WeakMap, NOT
// userData: THREE's Texture.copy/clone treatment of userData is not a contract
// we want to depend on (the KTX2 placeholder path copy()s the loaded texture
// over itself), and a ready-Promise must never go through a JSON round-trip.
//   texture → { rowId, linear, url, edge, ready: Promise }
const _texMeta = new WeakMap()

// Pass-1 load tracking: buildMaterialsFromAssets resets these; AvatarModel
// awaits whenTexturesSettled() and reads getLastBuildTextureUrls() to measure
// real download throughput off the resource-timing entries.
let _pendingLoads = []
let _lastBuildUrls = []

// rowId → max edge currently bound on the avatar (Infinity = full res).
// Drives the content browser's "displayed" column in the Resolutions panel.
const _boundEdgeByRow = new Map()

// KTX2/Basis (ENG-753). Lazily created once we have a renderer so the loader
// can detect which GPU-compressed formats this device supports. Until then
// (and on devices without basis support) we fall back to the original PNG/JPG.
let _ktx2Loader = null

// initKTX2 wires the KTX2 transcoder. Called once by AvatarModel with the R3F
// renderer. Idempotent. Transcoder wasm/js is served locally from /basis/
// (mirrors the local /draco/ decoder), so no CDN dependency.
export function initKTX2(renderer) {
  if (_ktx2Loader || !renderer) return
  _ktx2Loader = new KTX2Loader().setTranscoderPath('/basis/').detectSupport(renderer)
}

function _loadAssetTexture(url, { linear = false } = {}, loader = _texLoader) {
  if (!url) return null
  const key = url + (linear ? '#L' : '')
  if (_textureCache.has(key)) {
    // Cache hit: no new download (so no throughput sample), but the build's
    // settle barrier still has to cover an in-flight load from a prior call.
    const hit = _textureCache.get(key)
    const ready = _texMeta.get(hit)?.ready
    if (ready) _pendingLoads.push(ready)
    return hit
  }
  // THREE.Cache is shared across modules; clear so we don't pull a copy
  // loaded with the wrong colorSpace earlier in this page's lifecycle.
  THREE.Cache.remove(url)
  const cs = linear ? THREE.NoColorSpace : THREE.SRGBColorSpace

  // KTX2Loader, unlike TextureLoader, returns nothing from load() and delivers
  // the transcoded CompressedTexture via the onLoad callback. Hand back a
  // placeholder immediately and copy the result in when it arrives — the
  // material keeps the same texture instance; needsUpdate re-uploads it.
  if (loader === _ktx2Loader) {
    const tex = new THREE.CompressedTexture()
    tex.colorSpace = cs
    tex.flipY = false
    applyDefaultTextureWrap(tex)
    const ready = new Promise((resolve) => {
      loader.load(url, (loaded) => {
        tex.copy(loaded)
        tex.colorSpace = cs
        tex.flipY = false
        // copy() overwrote wrap from the decoded texture — re-assert the policy.
        applyDefaultTextureWrap(tex)
        tex.needsUpdate = true
        loaded.dispose?.()
        resolve(true)
      }, undefined, (err) => {
        console.warn(`[avatarMaterial] KTX2 decode failed (${url}); leaving placeholder:`, err?.message || err)
        resolve(false)
      })
    })
    _registerLoad(tex, url, ready)
    _textureCache.set(key, tex)
    return tex
  }

  let tex
  const ready = new Promise((resolve) => {
    tex = loader.load(url, () => resolve(true), undefined, (err) => {
      console.warn(`[avatarMaterial] texture load failed (${url}):`, err?.message || err)
      resolve(false)
    })
  })
  tex.colorSpace = cs
  tex.flipY = false
  applyDefaultTextureWrap(tex)
  _registerLoad(tex, url, ready)
  _textureCache.set(key, tex)
  return tex
}

// Track a fresh load for whenTexturesSettled() / throughput measurement.
function _registerLoad(tex, url, ready) {
  _texMeta.set(tex, { ..._texMeta.get(tex), url, ready })
  _pendingLoads.push(ready)
  _lastBuildUrls.push(url)
}

// whenTexturesSettled resolves once every texture requested since the last
// buildMaterialsFromAssets call has loaded or failed. Failures resolve (false)
// rather than reject — the cascade must proceed with whatever arrived.
export function whenTexturesSettled() {
  return Promise.allSettled(_pendingLoads.slice())
}

// getLastBuildTextureUrls — the URLs the last build requested; AvatarModel
// matches these against performance resource-timing entries to measure real
// download throughput for the quality manager.
export function getLastBuildTextureUrls() {
  return _lastBuildUrls.slice()
}

// Load a texture from a texture_assets row, preferring its KTX2 variant at
// the requested quality tier (ENG-757). Loud fallback: if the KTX2 variant
// isn't ready we use the original file; a runtime KTX2 decode failure surfaces
// via three's own console error — never silently swapped for fake data.
function _loadTextureFromRow(row, opts = {}, tier = 'ultra') {
  // A per-texture pin (local to this browser) beats the build/global tier.
  const effTier = getTextureOverride(row.id) ?? tier
  const src = chooseTextureSource(row, !!_ktx2Loader, effTier)
  if (!src) return null
  if (src.ktx2) {
    // A KTX2 variant must never be able to break the material. If the loader
    // throws (transcoder init, unsupported format, decode), fall back to the
    // original PNG/JPG — loudly, so the real reason is visible (ENG-753).
    try {
      const tex = _loadAssetTexture(src.url, opts, _ktx2Loader)
      if (tex) {
        _tagRowMeta(tex, row, opts, src)
        return tex
      }
    } catch (e) {
      console.warn(`[avatarMaterial] KTX2 load failed (${src.url}); using original. Reason:`, e?.message || e)
    }
  }
  if (!row.file_url) return null
  const tex = _loadAssetTexture(row.file_url, opts, _texLoader)
  if (tex) _tagRowMeta(tex, row, opts, { url: row.file_url, edge: Infinity })
  return tex
}

// Record which row/tier a texture instance came from so the upgrade pass can
// re-resolve it at a different tier later.
function _tagRowMeta(tex, row, opts, src) {
  _texMeta.set(tex, {
    ..._texMeta.get(tex),
    rowId: row.id,
    linear: !!opts?.linear,
    url: src.url,
    edge: src.edge,
  })
  _boundEdgeByRow.set(row.id, src.edge)
}

// getTextureResidency — per-rung local/remote state for one texture row, for
// the content browser's Resolutions panel. `downloaded` means the file is in
// this session's texture cache; `boundEdge` is what the avatar is showing.
export function getTextureResidency(row) {
  if (!row) return null
  const inCache = (url) => !!url && (_textureCache.has(url) || _textureCache.has(url + '#L'))
  const rungs = []
  const variants = row.ktx2_variants
  if (variants && typeof variants === 'object') {
    for (const [key, entry] of Object.entries(variants)) {
      const edge = Number(key)
      if (!Number.isFinite(edge)) continue
      rungs.push({
        edge,
        status: entry?.status ?? '',
        byteSize: entry?.byte_size ?? null,
        downloaded: entry?.status === 'done' && inCache(entry.url),
      })
    }
  }
  rungs.sort((a, b) => a.edge - b.edge)
  rungs.push({
    edge: Infinity,
    status: row.ktx2_status === 'done' ? 'done' : (row.ktx2_status || 'original only'),
    byteSize: row.ktx2_byte_size ?? row.byte_size ?? row.byteSize ?? null,
    downloaded: inCache(row.ktx2_url) || inCache(row.file_url),
  })
  return { boundEdge: _boundEdgeByRow.get(row.id) ?? null, rungs }
}

// If d[<slotKey>Offset|Tiling|Rotation] specifies a non-identity transform,
// return a clone of the base texture with that transform applied. Cloning
// preserves the GPU upload (shares the .image / .source) but lets each
// material own its own offset/repeat/rotation matrix.
function _maybeCloneWithUV(base, d, slotKey) {
  const off = d[slotKey + 'Offset']
  const til = d[slotKey + 'Tiling']
  const rotDeg = d[slotKey + 'Rotation']
  const hasOff = Array.isArray(off) && off.length === 2 && (off[0] !== 0 || off[1] !== 0)
  const hasTil = Array.isArray(til) && til.length === 2 && (til[0] !== 1 || til[1] !== 1)
  const hasRot = Number.isFinite(rotDeg) && rotDeg !== 0
  if (!hasOff && !hasTil && !hasRot) return base
  const clone = base.clone()
  // Clones share the GPU upload but not the WeakMap entry — carry the cascade
  // provenance over so the upgrade pass can re-resolve them too.
  if (_texMeta.has(base)) _texMeta.set(clone, _texMeta.get(base))
  clone.needsUpdate = true
  if (hasOff) clone.offset.fromArray(off)
  if (hasTil) clone.repeat.fromArray(til)
  if (hasRot) clone.rotation = rotDeg * Math.PI / 180
  return clone
}

function _cullToSide(cull) {
  switch (cull) {
    case 0: return THREE.DoubleSide   // none
    case 3: return THREE.DoubleSide   // both
    case 2: return THREE.BackSide     // cull front
    default: return THREE.FrontSide   // 1 = cull back (default)
  }
}

// PBR materials are MeshStandardMaterial by default; promote to
// MeshPhysicalMaterial only when the artist enables a feature that
// requires it (specular maps, clearcoat). Lets ordinary PBR stay on the
// cheaper class.
function _needsPhysical(d) {
  return !!(d.specularIntensityMap || d.specularColorMap || d.clearcoatEnabled)
}

// ─── Live material list (EnvProbe consumes this) ───────────────────────────

let _activeMaterials = []   // flat list of every material currently bound

export function getAvatarMaterials() {
  return { _all: _activeMaterials }
}

// Advance time-driven graph-material uniforms. Called once per frame from
// AvatarModel.useFrame. Mutates the existing uniform objects in place — no
// allocation, no React state (CLAUDE.md hard rule).
export function tickGraphUniforms(elapsedSeconds) {
  for (const m of _activeMaterials) {
    const u = m.userData?.graphUniforms
    if (u?.uTime) u.uTime.value = elapsedSeconds
  }
}

// Live-update one exposed graph param by (assetId, nodeId, handle). Mutates the
// uniform in place — no recompile, no React state. vec3 params accept an array.
// Returns true if the param was found and applied.
export function setGraphParam(assetId, nodeId, handle, value) {
  const mat = _activeMaterials.find(m => m.userData?.assetId === assetId && m.userData?.graphParams)
  if (!mat) return false
  const entry = mat.userData.graphParams[`${nodeId}:${handle}`]
  if (!entry || !entry.uniformObj) return false
  if (Array.isArray(value)) {
    if (entry.uniformObj.value && entry.uniformObj.value.fromArray) entry.uniformObj.value.fromArray(value)
    else entry.uniformObj.value = new THREE.Vector3().fromArray(value)
  } else {
    entry.uniformObj.value = value
  }
  return true
}

// Feed a PMREM env map into toon-graph materials (MeshToon has no native
// envMap). Sets the uniform + toggles USE_TOON_ENV and the CubeUV defines the
// injected cube_uv_reflection_fragment needs. One recompile per env change —
// never per frame. Passing null clears the env define.
export function setToonEnvOnAll(envTexture, envMapIntensity = 1) {
  for (const mat of _activeMaterials) {
    if (!mat?.userData?.isToonGraph) continue
    const u = mat.userData.graphUniforms
    if (!u?.uToonEnvMap) continue
    u.uToonEnvMap.value = envTexture ?? null
    if (u.uToonEnvIntensity) u.uToonEnvIntensity.value = envMapIntensity
    mat.defines = mat.defines || {}
    if (envTexture && envTexture.image) {
      // Mirror three's generateCubeUVSize (WebGLProgram.js) so textureCubeUV's
      // mip math matches the PMREM packing.
      const h = envTexture.image.height
      const maxMip = Math.log2(h) - 2
      mat.defines.USE_TOON_ENV = ''
      // three's cube_uv_reflection_fragment gates textureCubeUV behind
      // ENVMAP_TYPE_CUBE_UV — without it the function is never declared and the
      // env recompile fails. Set it alongside the CubeUV mip defines.
      mat.defines.ENVMAP_TYPE_CUBE_UV = ''
      mat.defines.CUBEUV_TEXEL_WIDTH = (1.0 / (3 * Math.max(2 ** maxMip, 7 * 16))).toPrecision(9)
      mat.defines.CUBEUV_TEXEL_HEIGHT = (1.0 / h).toPrecision(9)
      mat.defines.CUBEUV_MAX_MIP = maxMip.toFixed(1)
    } else {
      delete mat.defines.USE_TOON_ENV
      delete mat.defines.ENVMAP_TYPE_CUBE_UV
      delete mat.defines.CUBEUV_TEXEL_WIDTH
      delete mat.defines.CUBEUV_TEXEL_HEIGHT
      delete mat.defines.CUBEUV_MAX_MIP
    }
    mat.needsUpdate = true
  }
}

// Update toon-env intensity without recompiling. Pure uniform write (no define
// toggle, no shader recompile) — called when envMapIntensity changes on the
// live tune slider. Only touches uToonEnvIntensity on toon-graph materials.
export function setToonEnvIntensityOnAll(intensity) {
  for (const mat of _activeMaterials) {
    const u = mat?.userData?.graphUniforms
    if (mat?.userData?.isToonGraph && u?.uToonEnvIntensity) u.uToonEnvIntensity.value = intensity
  }
}

// ─── Live material tuning (LiveTunePanel PBR/Skin knobs) ────────────────────
//
// The Tune Panel's pbr / pbrSkin sliders are GLOBAL look-dev multipliers over
// the per-material authored values. Before ENG-812 they wrote to liveTuneStore
// but nothing applied them — the "Normal Scale" slider did nothing, so normal
// maps read as unresponsive to lighting. This is the missing consumer.
//
// Applied in place (no React state, no recompile — same discipline as
// tickGraphUniforms / setEnvIntensityOnAll). Routes by userData.materialKind,
// which buildMaterialsFromAssets stamps from the material_assets `type`.
//   skin → pbrSkin (normalScale ×, roughness ×, thickness absolute)
//   pbr  → pbr     (roughness ×, metalness ×)
// toon/graph are left alone (own shading paths).

let _tuning = null   // last { pbr, pbrSkin } snapshot; re-applied to new builds

// Per-material AUTHORED baseline. The global Tune-Panel knobs are multipliers
// over this, never over the already-scaled value (no drift). Crucially the
// per-material Content-Browser inspector (bumpMapFactor / roughness / etc.) is
// the AUTHORITATIVE owner of these values: its live edits update this base via
// _rebaseAndApply, so the global knob composes with the inspector instead of
// clobbering it. (Pre-follow-up bug: the global re-applied a frozen base and
// wiped every inspector edit — "Normal Strength does nothing", ENG-812.)
function _tuneBase(mat) {
  if (!mat.userData._tuneBase) {
    mat.userData._tuneBase = {
      roughness: mat.roughness ?? 1,
      metalness: mat.metalness ?? 0,
      normalX: mat.normalScale?.x ?? 1,
      normalY: mat.normalScale?.y ?? 1,
      thickness: mat.thickness ?? 0.5,
    }
  }
  return mat.userData._tuneBase
}

function _applyTuningTo(mat) {
  if (!_tuning || !mat) return
  const kind = mat.userData?.materialKind ?? 'pbr'
  const base = _tuneBase(mat)
  if (kind === 'skin') {
    const s = _tuning.pbrSkin
    if (!s) return
    if (mat.normalScale && s.normalScale != null) {
      mat.normalScale.set(base.normalX * s.normalScale, base.normalY * s.normalScale)
    }
    if (s.roughness != null) mat.roughness = base.roughness * s.roughness
    if (s.thickness != null && mat.isMeshPhysicalMaterial) mat.thickness = base.thickness * s.thickness
  } else if (kind === 'pbr') {
    const p = _tuning.pbr
    if (!p) return
    if (p.roughness != null) mat.roughness = base.roughness * p.roughness
    if (p.metalness != null) mat.metalness = base.metalness * p.metalness
  }
}

// Record a per-material authored value edited live by the inspector, then
// re-apply the global knob so the visible result stays = authored × knob.
// The direct THREE-prop write in _LIVE_SCALAR has already landed; this just
// keeps the base honest so the next setMaterialTuning() doesn't revert it.
function _rebaseAndApply(mat, field, value) {
  const base = _tuneBase(mat)
  if (field === 'normal') { base.normalX = value; base.normalY = value }
  else base[field] = value
  _applyTuningTo(mat)
}

// setMaterialTuning — called by the LiveTunePanel sync (Scene.jsx) whenever the
// pbr/pbrSkin slices change. Caches the snapshot so freshly rebuilt materials
// (character swap, tier upgrade) pick up the current tuning too.
export function setMaterialTuning(tuning) {
  _tuning = tuning
  for (const mat of _activeMaterials) _applyTuningTo(mat)
}

// ─── Builders ──────────────────────────────────────────────────────────────

// Build one THREE.Material from a material_assets row + a textureById lookup.
// The returned material has userData.assetId set in buildMaterialsFromAssets
// so the live-preview path (livePreviewMaterial) can find it by row id.
export function buildMaterialFromAsset(materialAsset, textureById, tier = 'ultra') {
  const d = materialAsset.data ?? {}
  const type = materialAsset.type ?? 'pbr'
  // resolve(uuid, opts, slotKey?) — if slotKey is provided and the data has
  // <slotKey>Offset / <slotKey>Tiling / <slotKey>Rotation set to non-identity,
  // returns a cloned THREE.Texture with that transform applied. Cloning is
  // necessary because the underlying _loadAssetTexture cache shares one
  // texture instance per URL across all materials — without the clone, two
  // materials at different tilings would step on each other.
  const resolve = (uuid, opts, slotKey) => {
    if (!uuid) return null
    const row = textureById.get(uuid)
    if (!row) return null
    const base = _loadTextureFromRow(row, opts, tier)
    if (!base || !slotKey) return base
    return _maybeCloneWithUV(base, d, slotKey)
  }
  const baseColor = new THREE.Color().fromArray(Array.isArray(d.diffuse) && d.diffuse.length === 3 ? d.diffuse : [1, 1, 1])
  const opacity = d.opacity ?? 1
  const transparent = opacity < 1 || (d.blendType ?? 3) !== 3
  const side = _cullToSide(d.cull ?? 1)

  if (type === 'graph') {
    return buildGraphMaterial(d, textureById, tier)
  }
  if (type === 'toon') {
    const mat = new THREE.MeshToonMaterial({
      color: baseColor,
      map: resolve(d.diffuseMap, undefined, 'diffuseMap'),
      transparent, opacity, alphaTest: d.alphaTest ?? 0, side,
      gradientMap: getToonRamp(d.toonRamp ?? 3),
    })
    applyTextureAdjust(mat, d)
    return mat
  }
  if (type === 'skin') {
    const mat = new THREE.MeshPhysicalMaterial({
      color: baseColor,
      map: resolve(d.diffuseMap, undefined, 'diffuseMap'),
      normalMap: resolve(d.normalMap, { linear: true }, 'normalMap'),
      roughnessMap: resolve(d.glossMap, { linear: true }, 'glossMap'),
      roughness: d.roughness ?? 0.6,
      metalness: 0,
      transparent, opacity, alphaTest: d.alphaTest ?? 0, side,
      transmission: 0,
      thickness: d.subsurfaceAmount ?? 0.5,
    })
    const thickness = resolve(d.thicknessMap, { linear: true }, 'thicknessMap')
    if (thickness) mat.thicknessMap = thickness
    if (Array.isArray(d.subsurfaceColor) && d.subsurfaceColor.length === 3) {
      mat.attenuationColor = new THREE.Color().fromArray(d.subsurfaceColor)
      mat.attenuationDistance = 0.6
    }
    if (mat.normalMap && d.bumpMapFactor != null) {
      mat.normalScale = new THREE.Vector2(d.bumpMapFactor, d.bumpMapFactor)
    }
    _applyOrmPacking(mat, d, resolve)
    applyTextureAdjust(mat, d)
    return mat
  }
  // pbr (default)
  const Cls = _needsPhysical(d) ? THREE.MeshPhysicalMaterial : THREE.MeshStandardMaterial
  const mat = new Cls({
    color: baseColor,
    map: resolve(d.diffuseMap, undefined, 'diffuseMap'),
    normalMap: resolve(d.normalMap, { linear: true }, 'normalMap'),
    roughnessMap: resolve(d.glossMap, { linear: true }, 'glossMap'),
    metalnessMap: resolve(d.metalnessMap, { linear: true }, 'metalnessMap'),
    aoMap: resolve(d.aoMap, { linear: true }, 'aoMap'),
    emissive: new THREE.Color().fromArray(Array.isArray(d.emissive) && d.emissive.length === 3 ? d.emissive : [0, 0, 0]),
    emissiveMap: resolve(d.emissiveMap, undefined, 'emissiveMap'),
    emissiveIntensity: d.emissiveIntensity ?? 1,
    roughness: d.roughness ?? 0.5,
    metalness: d.metalness ?? 0,
    transparent, opacity, alphaTest: d.alphaTest ?? 0, side,
  })
  if (mat.normalMap && d.bumpMapFactor != null) {
    mat.normalScale = new THREE.Vector2(d.bumpMapFactor, d.bumpMapFactor)
  }
  _applyOrmPacking(mat, d, resolve)
  _applySpecular(mat, d, resolve)
  _applyClearcoat(mat, d, resolve)
  applyTextureAdjust(mat, d)
  return mat
}

// Compile a node graph (d.graph) into a MeshStandardMaterial with onBeforeCompile
// injection. Texture-sample uniforms are resolved here (textureId -> THREE.Texture).
// On a compile error we throw so buildMaterialsFromAssets logs it; the mesh then
// has no built material, so it reports as `unresolved` and wears the neutral
// placeholder — never a silent fake, and never another mesh's look (CLAUDE.md UX
// rule; materialBinding.js).
function buildGraphMaterial(d, textureById, tier = 'ultra') {
  const compiled = compileMaterialGraph(d.graph ?? { nodes: [], edges: [] })
  if (compiled.errors.length) {
    throw new Error('material graph: ' + compiled.errors.join('; '))
  }
  const isToon = compiled.shaderModel === 'toon'

  // Output-node inline data holds the build-time + material-level controls
  // (toon bands/smoothness, and the blend mode / clip / two-sided below).
  const outNode = (d.graph?.nodes ?? []).find(n => n.type === 'ToonBSDF' || n.type === 'PrincipledBSDF')
  const outData = outNode?.data ?? {}

  let mat
  if (isToon) {
    const bands = outData.bands ?? 3
    const smoothness = outData.smoothness ?? 0
    mat = new THREE.MeshToonMaterial({ gradientMap: getToonRamp(bands, smoothness) })
  } else {
    mat = new THREE.MeshStandardMaterial({ roughness: 0.5, metalness: 0 })
  }

  if (compiled.needsUv) mat.defines = { ...(mat.defines || {}), USE_UV: '' }

  const graphUniforms = {}
  for (const [name, u] of Object.entries(compiled.uniforms)) {
    if (u.textureId !== undefined) {
      const row = textureById.get(u.textureId)
      const tex = row ? _loadTextureFromRow(row, { linear: !!u.linear }, tier) : null
      graphUniforms[name] = { value: tex }
    } else if (u.type === 'vec2' || (Array.isArray(u.value) && u.value.length === 2)) {
      graphUniforms[name] = { value: new THREE.Vector2().fromArray(u.value) }
    } else if (Array.isArray(u.value)) {
      graphUniforms[name] = { value: new THREE.Vector3().fromArray(u.value) }
    } else {
      graphUniforms[name] = { value: u.value ?? 0 }
    }
  }
  if (compiled.needsTime) graphUniforms.uTime = { value: 0 }
  if (isToon) {
    // MeshToon has no native envMap; env is fed here by EnvProbe/Reflection.
    graphUniforms.uToonEnvMap = { value: null }
    graphUniforms.uToonEnvIntensity = { value: 1 }
  }

  const _graphCacheKey = 'graph:' + JSON.stringify(d.graph)

  mat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, graphUniforms)
    shader.fragmentShader = isToon
      ? assembleToonShader(shader.fragmentShader, compiled)
      : assembleGraphShader(shader.fragmentShader, compiled)
    shader.vertexShader = assembleGraphVertex(shader.vertexShader, compiled)
  }
  mat.userData.graphUniforms = graphUniforms
  mat.userData.isGraphMaterial = true
  if (isToon) mat.userData.isToonGraph = true
  const graphParams = {}
  for (const p of compiled.params ?? []) {
    graphParams[`${p.nodeId}:${p.handle}`] = { ...p, uniformObj: graphUniforms[p.uniform] }
  }
  mat.userData.graphParams = graphParams
  mat.customProgramCacheKey = () => _graphCacheKey

  // Opacity mask / blend mode (Unreal-style), stored on the output node so the
  // graph — and its live preview — is the single source of truth. The graph's
  // `opacity` output writes diffuseColor.a; these props decide whether that
  // alpha hard-discards (masked) or blends (translucent). Set before first
  // compile so three picks up USE_ALPHATEST for masked materials.
  const { transparent, alphaTest } = graphBlendMode(outData)
  mat.transparent = transparent
  mat.alphaTest = alphaTest
  mat.side = outData.twoSided ? THREE.DoubleSide : THREE.FrontSide

  return mat
}

// Clearcoat / wet layer — opt-in via d.clearcoatEnabled. The presence of
// this flag promotes the underlying class to MeshPhysicalMaterial via
// _needsPhysical(d); when off, ordinary PBR stays on MeshStandardMaterial.
function _applyClearcoat(mat, d, resolve) {
  if (!d.clearcoatEnabled || !mat.isMeshPhysicalMaterial) return
  mat.clearcoat = d.clearcoat ?? 1
  mat.clearcoatRoughness = d.clearcoatRoughness ?? 0
  const cm = resolve(d.clearcoatMap, { linear: true }, 'clearcoatMap')
  if (cm) mat.clearcoatMap = cm
  const crm = resolve(d.clearcoatRoughnessMap, { linear: true }, 'clearcoatRoughnessMap')
  if (crm) mat.clearcoatRoughnessMap = crm
}

// Specular maps live on MeshPhysicalMaterial (glTF KHR_materials_specular).
// Only applied when the material was promoted by _needsPhysical(); silently
// ignored on plain MeshStandardMaterial so the field can exist on the asset
// without forcing class promotion.
function _applySpecular(mat, d, resolve) {
  if (!mat.isMeshPhysicalMaterial) return
  if (d.specularIntensity != null) mat.specularIntensity = d.specularIntensity
  if (Array.isArray(d.specularColor) && d.specularColor.length === 3) {
    mat.specularColor = new THREE.Color().fromArray(d.specularColor)
  }
  const sim = resolve(d.specularIntensityMap, { linear: true }, 'specularIntensityMap')
  if (sim) mat.specularIntensityMap = sim
  const scm = resolve(d.specularColorMap, undefined, 'specularColorMap') // sRGB — colored tint
  if (scm) mat.specularColorMap = scm
}

// When d.mapPacking === 'orm', bind a single ORM texture to ao/rough/metal
// slots. Three.js samples the matching channel via aoMapChannel etc.; the
// glTF spec packs R=AO, G=Roughness, B=Metalness. Runs *after* the
// individual-map assignment so ORM wins when enabled, but leaves the
// individual binding in place when the artist switches back to 'individual'.
function _applyOrmPacking(mat, d, resolve) {
  if (d.mapPacking !== 'orm' || !d.ormMap) return
  const orm = resolve(d.ormMap, { linear: true }, 'ormMap')
  if (!orm) return
  mat.aoMap = orm
  mat.roughnessMap = orm
  mat.metalnessMap = orm
  mat.needsUpdate = true
}

// Build a meshName → THREE.Material map plus a hidden-mesh set, given the
// raw material_assets, texture_assets, and material_mapping arrays straight
// off the API. Caller (AvatarModel) hands the result to applyMaterialMapping.
export function buildMaterialsFromAssets({ materials = [], textures = [], mapping = [], tier = 'ultra' }) {
  // Fresh settle barrier per build (ENG-757): whenTexturesSettled() and the
  // throughput measurement scope to the loads this call kicks off.
  _pendingLoads = []
  _lastBuildUrls = []
  const textureById = new Map(textures.map(t => [t.id, t]))
  const matById = new Map()
  for (const m of materials) {
    try {
      const built = buildMaterialFromAsset(m, textureById, tier)
      built.userData.assetId = m.id          // for livePreviewMaterial lookup
      built.userData.materialKind = m.type ?? 'pbr'  // routes setMaterialTuning
      matById.set(m.id, built)
    }
    catch (e) { console.warn(`[avatarMaterial] failed to build material ${m.name}:`, e) }
  }
  const meshNameToMaterial = new Map()
  const hiddenMeshNames = new Set()
  // The three sets applyMaterialMapping classifies against. `mappedMeshNames`
  // is every row whatever its state, so a mesh the mapping never mentions is
  // distinguishable from one it mentions and leaves empty — see
  // materialBinding.js. There is deliberately NO `fallback` here any more: it
  // was always Element 0, and handing it to unbound meshes is the bug this
  // separation exists to make impossible.
  const mappedMeshNames = new Set()
  const unresolvedMeshNames = new Set()
  for (const entry of mapping) {
    mappedMeshNames.add(entry.meshName)
    if (entry.hidden) {
      hiddenMeshNames.add(entry.meshName)
      continue
    }
    if (entry.materialId) {
      const mat = matById.get(entry.materialId)
      if (mat) meshNameToMaterial.set(entry.meshName, mat)
      // A materialId naming no built material is a DANGLING reference, not an
      // empty slot. Recorded apart so the console can say which it is.
      else unresolvedMeshNames.add(entry.meshName)
    }
  }
  // Refresh the flat list EnvProbe reads. Dedup since multiple meshes
  // legitimately share the same material instance.
  _activeMaterials = Array.from(new Set(meshNameToMaterial.values()))
  // Re-apply the current live tuning so a fresh build (character swap, reload)
  // inherits the artist's pbr/pbrSkin slider state instead of snapping back to
  // authored values.
  for (const mat of _activeMaterials) _applyTuningTo(mat)
  return { meshNameToMaterial, hiddenMeshNames, mappedMeshNames, unresolvedMeshNames, matById }
}

// ─── Quality-tier upgrade pass (ENG-757) ────────────────────────────────────
//
// Walks every active material's texture slots, re-resolves each bound texture
// against the target tier, and hot-swaps the slot once the replacement has
// loaded. Per-slot swap — no material rebuild, no shader recompile, no flicker.
// Downgrades are refused unless allowDowngrade (the user manually picking a
// lower tier). Runs entirely in async load paths — nothing per-frame.

// The fixed texture-slot list the builders can populate.
const _TEXTURE_SLOTS = [
  'map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap',
  'thicknessMap', 'clearcoatMap', 'clearcoatRoughnessMap',
  'specularIntensityMap', 'specularColorMap',
]

// upgradeActiveMaterialsTo re-targets all bound textures at `targetTier`.
// textureRows are the texture_assets rows (same array the build used, or a
// re-fetched one). Resolves with the number of slots actually swapped.
export async function upgradeActiveMaterialsTo(targetTier, textureRows, { allowDowngrade = false } = {}) {
  const rowById = new Map((textureRows ?? []).map(r => [r.id, r]))
  const swaps = []

  const planSwap = (currentTex, assign) => {
    const meta = _texMeta.get(currentTex)
    if (!meta || meta.rowId === undefined) return
    const row = rowById.get(meta.rowId)
    if (!row) return
    // A per-texture pin (local to this browser) beats the global target, and
    // reaching a pin is always allowed in both directions — it's explicit.
    const override = getTextureOverride(meta.rowId)
    const rowTier = override ?? targetTier
    const src = chooseTextureSource(row, !!_ktx2Loader, rowTier)
    if (!shouldSwapTexture(meta, src, allowDowngrade || override !== null)) return
    swaps.push(async () => {
      const next = _loadAssetTexture(src.url, { linear: meta.linear }, src.ktx2 ? _ktx2Loader : _texLoader)
      if (!next) return false
      _texMeta.set(next, { ..._texMeta.get(next), rowId: meta.rowId, linear: meta.linear, url: src.url, edge: src.edge })
      const ok = await (_texMeta.get(next)?.ready ?? Promise.resolve(true))
      tickUpgradeProgress()
      if (!ok) {
        console.warn(`[avatarMaterial] tier upgrade load failed (${src.url}); keeping current texture`)
        return false
      }
      // Preserve a UV-transformed clone's transform: the replacement inherits
      // the exact offset/repeat/rotation of what it replaces.
      let bound = next
      const hasUV = currentTex.rotation !== 0 ||
        currentTex.offset.x !== 0 || currentTex.offset.y !== 0 ||
        currentTex.repeat.x !== 1 || currentTex.repeat.y !== 1
      if (hasUV) {
        bound = next.clone()
        _texMeta.set(bound, _texMeta.get(next))
        bound.offset.copy(currentTex.offset)
        bound.repeat.copy(currentTex.repeat)
        bound.rotation = currentTex.rotation
        bound.needsUpdate = true
      }
      assign(bound)
      _boundEdgeByRow.set(meta.rowId, src.edge)
      return true
    })
  }

  for (const mat of _activeMaterials) {
    for (const slot of _TEXTURE_SLOTS) {
      const tex = mat[slot]
      if (tex && tex.isTexture) {
        planSwap(tex, (next) => {
          // Re-check: another upgrade may have swapped this slot while we
          // were loading — last writer wins is fine, stale writer is not.
          if (mat[slot] === tex) {
            mat[slot] = next
            mat.needsUpdate = true
          }
        })
      }
    }
    const uniforms = mat.userData?.graphUniforms
    if (uniforms) {
      for (const u of Object.values(uniforms)) {
        const tex = u?.value
        if (tex && tex.isTexture) {
          planSwap(tex, (next) => {
            if (u.value === tex) u.value = next
          })
        }
      }
    }
  }

  beginUpgradeProgress(swaps.length)
  try {
    const results = await Promise.all(swaps.map(fn => fn()))
    return results.filter(Boolean).length
  } finally {
    endUpgradeProgress()
  }
}

// ─── Live preview ─────────────────────────────────────────────────────────
//
// Mutate the THREE material that backs a material_assets row in place,
// without re-fetching from the server or rebuilding from scratch. Called
// by MaterialInspector on every slider tick so the avatar updates in real
// time. Server PATCH still happens (debounced) for persistence.
//
// Returns true if every field in `patch` was applied live. Returns false if
// at least one field needs a full rebuild (texture-slot binding, class
// promotion, blend mode, etc.) — caller falls back to __RELOAD_MATERIALS__.

// Fields whose live update can mutate THREE props directly.
// Handlers that map onto a value the global Tune-Panel knob also scales
// (roughness, metalness, normal strength, thickness) must _rebaseAndApply so
// the authored base tracks the edit — otherwise the next setMaterialTuning()
// reverts it (the "Normal Strength does nothing" bug). Handlers for props no
// global knob touches (opacity, alphaTest, …) just write the prop directly.
const _LIVE_SCALAR = {
  metalness:          (m, v) => { m.metalness = v; _rebaseAndApply(m, 'metalness', v) },
  roughness:          (m, v) => { m.roughness = v; _rebaseAndApply(m, 'roughness', v) },
  opacity:            (m, v) => { m.opacity = v; m.transparent = v < 1 || (m.userData?._blendType ?? 3) !== 3 },
  alphaTest:          (m, v) => { m.alphaTest = v },
  emissiveIntensity:  (m, v) => { m.emissiveIntensity = v },
  bumpMapFactor:      (m, v) => { if (m.normalScale) m.normalScale.set(v, v); _rebaseAndApply(m, 'normal', v) },
  aoIntensity:        (m, v) => { m.aoMapIntensity = v },
  // Physical-only:
  clearcoat:          (m, v) => { if (m.isMeshPhysicalMaterial) m.clearcoat = v },
  clearcoatRoughness: (m, v) => { if (m.isMeshPhysicalMaterial) m.clearcoatRoughness = v },
  specularIntensity:  (m, v) => { if (m.isMeshPhysicalMaterial) m.specularIntensity = v },
  // skin: subsurface amount drives MeshPhysicalMaterial.thickness
  subsurfaceAmount:   (m, v) => { if (m.isMeshPhysicalMaterial) m.thickness = v; _rebaseAndApply(m, 'thickness', v) },
}
const _LIVE_COLOR = {
  diffuse:        (m, rgb) => { m.color.fromArray(rgb) },
  emissive:       (m, rgb) => { m.emissive.fromArray(rgb) },
  specularColor:  (m, rgb) => { if (m.isMeshPhysicalMaterial) m.specularColor.fromArray(rgb) },
  subsurfaceColor:(m, rgb) => { if (m.isMeshPhysicalMaterial && m.attenuationColor) m.attenuationColor.fromArray(rgb) },
}
const _LIVE_TEX_ADJUST = new Set(['textureContrast', 'textureSaturation', 'textureValue', 'textureTint'])

// Keys that always require a full rebuild (texture-slot bindings, class
// promotion, shader chunks that aren't pre-installed, glTF state flips).
const _NEEDS_REBUILD = new Set([
  'mapPacking', 'ormMap', 'glossInvert',
  'clearcoatEnabled', 'clearcoatMap', 'clearcoatRoughnessMap',
  'diffuseMap', 'normalMap', 'metalnessMap', 'glossMap', 'aoMap', 'aoMapChannel',
  'emissiveMap', 'opacityMap', 'thicknessMap',
  'specularIntensityMap', 'specularColorMap',
  'diffuseMapTint', 'diffuseMapVertexColor',
  'blendType', 'depthTest', 'depthWrite', 'cull',
  'toonRamp', 'outlineColor', 'outlineWidth',
  // UV transforms — texture clones happen at build time.
])

export function livePreviewMaterial(assetId, patch) {
  if (!assetId || !patch) return false
  const target = _activeMaterials.find(m => m.userData?.assetId === assetId)
  if (!target) return false
  // Any UV-transform key (suffix Offset/Tiling/Rotation) forces rebuild.
  for (const key of Object.keys(patch)) {
    if (/(?:Offset|Tiling|Rotation)$/.test(key)) return false
    if (_NEEDS_REBUILD.has(key)) return false
  }
  // Apply each known field in-place.
  for (const [key, value] of Object.entries(patch)) {
    const scalarFn = _LIVE_SCALAR[key]
    if (scalarFn) { scalarFn(target, value); continue }
    const colorFn = _LIVE_COLOR[key]
    if (colorFn && Array.isArray(value) && value.length === 3) { colorFn(target, value); continue }
    if (_LIVE_TEX_ADJUST.has(key)) {
      if (!setTextureAdjustUniform(target, key, value)) return false
      continue
    }
    // Unknown field — bail and let the caller rebuild for safety.
    return false
  }
  target.needsUpdate = false   // scalar/color/uniform changes don't need a recompile
  return true
}

// Walk a rig scene and apply the material map. A mesh the mapping binds nothing
// to wears the NEUTRAL PLACEHOLDER and is reported — never another mesh's
// material. Until 2026-08-31 it inherited `fallback`, "the first mapping entry
// that resolved", which is Element 0 by construction; binding a material to
// Element 0 therefore repainted every unbound mesh on the character. The
// reasoning, and how PlayCanvas handles the same case, is in materialBinding.js.
// Hidden meshes are marked invisible.
//
// Returns the binding report (see emptyBindingReport) so a caller or a spec can
// assert on it; the console lines are emitted here so no caller can forget.
export function applyMaterialMapping(root, built) {
  const { meshNameToMaterial, hiddenMeshNames } = built
  const sets = {
    bound: new Set(meshNameToMaterial.keys()),
    hidden: hiddenMeshNames,
    unresolved: built.unresolvedMeshNames ?? new Set(),
    mapped: built.mappedMeshNames ?? new Set(meshNameToMaterial.keys()),
  }
  const report = emptyBindingReport()
  // NO mapping at all is not the same as a mapping that omits a mesh, and the
  // difference decides whether we may touch the scene's materials. The AAM
  // stores an uploaded rig WITHOUT parsing it (materialMappingFromGlb.js), so a
  // character can legitimately have zero rows — there is no binding intent to
  // honour, and the GLB's own embedded materials are the only statement of what
  // it should look like. Repainting the whole character grey there would be a
  // worse silent lie than the fallback this function exists to delete.
  const noMapping = sets.mapped.size === 0
  report.unmappedCharacter = noMapping

  root.traverse((node) => {
    if (!node.isMesh) return
    node.castShadow = true
    node.receiveShadow = true
    if (node.isSkinnedMesh) node.frustumCulled = false
    if (noMapping) return

    const state = classifyMeshBinding(node.name, sets)
    recordBinding(report, state, node.name)
    if (state === HIDDEN) {
      node.visible = false
      return
    }
    node.material = isUnbound(state) ? unboundMaterial() : meshNameToMaterial.get(node.name)
  })

  reportBinding(report)
  return report
}

// Last report, for console diagnosis and specs. Named for what it is rather
// than for one consumer — the same reason every other window.__* here is.
let _lastBindingSignature = null
function reportBinding(report) {
  if (typeof window !== 'undefined') window.__MATERIAL_BINDING__ = report
  // Two texture-tier passes bind the same scene, and every inspector PATCH
  // calls reloadMaterialsEverywhere, so an unchanged complaint would repeat
  // until it was noise. Log a given set of unbound meshes once.
  const sig = bindingSignature(report)
  if (sig === _lastBindingSignature) return
  _lastBindingSignature = sig
  for (const { level, text } of bindingMessages(report)) {
    (level === 'error' ? console.error : console.warn)(text)
  }
}
